import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  Search, Filter, Plus, ChevronDown, ChevronUp, Bell,
  Phone, Mail, MessageSquare, Calendar, Trash2, Save,
  Users, UserCheck, Handshake, AlertTriangle, Clock,
  Target, X, Send, Linkedin, Video, Building2, ShoppingCart,
  FileText, ArrowRight, ExternalLink, Tag, Link2, Megaphone,
  CheckCircle2, Edit, RefreshCw, Loader2, PhoneCall, MessageCircle,
  Hourglass, CalendarCheck, LayoutGrid, List, GripVertical, Check,
  Circle, Flame
} from 'lucide-react';

const STATUTS_CSE = [
  { value: 'a contacter', label: 'A contacter', color: '#3B82F6', bg: '#1e3a5f' },
  { value: 'contacte', label: 'Contacte', color: '#F59E0B', bg: '#3d2e0a' },
  { value: 'en discussion', label: 'En discussion', color: '#00E01A', bg: '#0a2e0a' },
  { value: 'partenaire', label: 'Partenaire', color: '#10B981', bg: '#0a2e1a' },
  { value: 'sans suite', label: 'Sans suite', color: '#6B7280', bg: '#1a1a1a' },
];

const STATUTS_CLIENT = [
  { value: 'a contacter', label: 'A contacter', color: '#3B82F6', bg: '#1e3a5f', icon: Phone, aliases: ['à contacter'] },
  { value: 'contacte', label: 'Contacte', color: '#F59E0B', bg: '#3d2e0a', icon: PhoneCall, aliases: ['contacté'] },
  { value: 'devis a faire', label: 'Devis a faire', color: '#8B5CF6', bg: '#2d1b69', icon: FileText, aliases: [] },
  { value: 'devis envoye', label: 'Devis envoye', color: '#06B6D4', bg: '#0a2a30', icon: Send, aliases: ['devis envoyé'] },
  { value: 'devis accepte', label: 'Devis accepte', color: '#00E01A', bg: '#0a2e0a', icon: CheckCircle2, aliases: ['devis accepté'] },
  { value: 'sans suite', label: 'Sans suite', color: '#6B7280', bg: '#1a1a1a', icon: X, aliases: [] },
];

function matchStatut(prospectStatut, colValue, colAliases = []) {
  const s = (prospectStatut || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const v = colValue.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  if (s === v) return true;
  return colAliases.some(a => s === a.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, ''));
}

const STATUTS = STATUTS_CSE;

const PRIORITES = [
  { value: 'urgent', label: 'Urgent', color: '#FF0040', glow: '0 0 12px rgba(255,0,64,0.8)', pulse: true },
  { value: 'haute', label: 'Haute', color: '#EF4444', glow: '0 0 8px rgba(239,68,68,0.6)' },
  { value: 'moyenne', label: 'Moyenne', color: '#F59E0B', glow: '0 0 8px rgba(245,158,11,0.4)' },
  { value: 'basse', label: 'Basse', color: '#10B981', glow: '0 0 8px rgba(16,185,129,0.4)' },
];

const TASK_PRESETS = [
  'Rappeler le client',
  'Envoyer devis',
  'Relancer par SMS',
  'Planifier RDV',
  'Envoyer documentation',
  'Suivi apres demo',
  'Confirmer commande',
];

const EXCHANGE_TYPES = [
  { value: 'email', label: 'Email', icon: Mail },
  { value: 'appel', label: 'Appel', icon: Phone },
  { value: 'linkedin', label: 'LinkedIn', icon: Linkedin },
  { value: 'rdv', label: 'Rendez-vous', icon: Video },
];

const AVATAR_COLORS = [
  '#EF4444', '#F59E0B', '#10B981', '#3B82F6', '#8B5CF6',
  '#EC4899', '#06B6D4', '#F97316', '#14B8A6', '#A855F7',
];

const RW_EMAIL = 'riding.watt83@gmail.com';

function getInitials(name) {
  if (!name) return '?';
  return name.split(' ').map(w => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
}

function getStatutStyle(statut, isClient = false) {
  const list = isClient ? STATUTS_CLIENT : STATUTS_CSE;
  const found = list.find(s => matchStatut(statut, s.value, s.aliases || []));
  return found || list[0];
}

function getPrioriteStyle(prio) {
  return PRIORITES.find(p => p.value === prio) || PRIORITES[1];
}

function isOverdue(dateSuivi) {
  if (!dateSuivi) return false;
  return dateSuivi < new Date().toISOString().split('T')[0];
}

function isSoon(dateSuivi) {
  if (!dateSuivi) return false;
  const today = new Date();
  const soon = new Date(today);
  soon.setDate(today.getDate() + 3);
  return dateSuivi >= today.toISOString().split('T')[0] && dateSuivi <= soon.toISOString().split('T')[0];
}

// ============ STATS BAR ============
function StatsBar({ stats, isClient }) {
  const items = isClient ? [
    { label: 'Total', value: stats.total, icon: ShoppingCart, color: '#FFFFFF' },
    { label: 'Contactés', value: stats.contactes, icon: UserCheck, color: '#F59E0B' },
    { label: 'En discussion', value: stats.en_discussion, icon: Handshake, color: '#00E01A' },
    { label: 'Convertis', value: stats.partenaires, icon: Target, color: '#10B981' },
    { label: 'Relances en retard', value: stats.relances_retard, icon: AlertTriangle, color: '#EF4444' },
  ] : [
    { label: 'Total', value: stats.total, icon: Users, color: '#FFFFFF' },
    { label: 'Contactés', value: stats.contactes, icon: UserCheck, color: '#F59E0B' },
    { label: 'En discussion', value: stats.en_discussion, icon: Handshake, color: '#00E01A' },
    { label: 'Partenaires', value: stats.partenaires, icon: Target, color: '#10B981' },
    { label: 'Relances en retard', value: stats.relances_retard, icon: AlertTriangle, color: '#EF4444' },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="prospects-stats-bar">
      {items.map(item => (
        <div key={item.label} className="bg-[#111111] rounded-xl border border-[#222] p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: item.color + '15' }}>
            <item.icon size={20} style={{ color: item.color }} />
          </div>
          <div>
            <p className="text-2xl font-bold text-white" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.05em' }}>{item.value}</p>
            <p className="text-[10px] uppercase tracking-wider text-[#8C8F94]" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>{item.label}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

// ============ REMINDER PANEL ============
function ReminderPanel({ reminders, onClose, onScrollTo }) {
  return (
    <div className="absolute right-0 top-12 w-80 bg-[#111111] border border-[#222] rounded-xl shadow-2xl z-50 overflow-hidden" data-testid="reminders-panel">
      <div className="flex items-center justify-between p-4 border-b border-[#222]">
        <h3 className="text-white font-bold text-sm" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Relances</h3>
        <button onClick={onClose} className="text-[#8C8F94] hover:text-white"><X size={16} /></button>
      </div>
      <div className="max-h-80 overflow-y-auto">
        {reminders.length === 0 ? (
          <p className="p-4 text-[#8C8F94] text-sm text-center">Aucune relance prévue</p>
        ) : (
          reminders.map(r => (
            <button
              key={r.id}
              onClick={() => { onScrollTo(r.id); onClose(); }}
              className="w-full text-left p-3 border-b border-[#1a1a1a] hover:bg-[#1a1a1a] transition-colors"
              data-testid={`reminder-${r.id}`}
            >
              <div className="flex items-center justify-between">
                <span className="text-white text-sm font-medium truncate">{r.org}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${r.overdue ? 'bg-[#2A1515] text-[#EF4444]' : 'bg-[#2A2A0A] text-[#F59E0B]'}`}>
                  {r.overdue ? 'En retard' : 'Bientôt'}
                </span>
              </div>
              <p className="text-[#8C8F94] text-xs mt-1">{r.dateSuivi ? new Date(r.dateSuivi + 'T00:00:00').toLocaleDateString('fr-FR') : '-'}</p>
            </button>
          ))
        )}
      </div>
    </div>
  );
}

// ============ EXCHANGE CARD ============
function ExchangeCard({ exchange, onDelete }) {
  const [collapsed, setCollapsed] = useState(true);
  const isSent = exchange.is_sent || exchange.auteur?.toLowerCase().includes(RW_EMAIL);
  const isGmail = exchange.source === 'gmail';
  const senderInitial = getInitials(exchange.auteur?.split('<')[0]?.trim() || exchange.auteur);

  return (
    <div
      className="rounded-lg overflow-hidden border border-[#222]"
      style={{ borderLeftWidth: 3, borderLeftColor: isSent ? '#00E01A' : '#3B82F6' }}
      data-testid={`exchange-${exchange.id}`}
    >
      <div
        className="flex items-center gap-3 p-3 cursor-pointer hover:bg-[#1a1a1a] transition-colors"
        onClick={() => setCollapsed(!collapsed)}
      >
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
          style={{ backgroundColor: isSent ? '#00E01A' : '#3B82F6' }}
        >
          {senderInitial}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-white text-sm font-medium truncate">{exchange.auteur?.split('<')[0]?.trim()}</span>
            {isSent && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#00E01A]/20 text-[#00E01A] font-semibold flex-shrink-0" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Envoyé</span>
            )}
            {isGmail && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#EF4444]/15 text-[#EF4444] font-semibold flex-shrink-0" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Gmail</span>
            )}
          </div>
          {exchange.sujet && <p className="text-[#8C8F94] text-xs truncate">{exchange.sujet}</p>}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="text-[#8C8F94] text-xs">{exchange.date ? new Date(exchange.date + 'T00:00:00').toLocaleDateString('fr-FR') : ''}</span>
          {!isGmail && (
            <button onClick={(e) => { e.stopPropagation(); onDelete(exchange.id); }} className="text-[#8C8F94] hover:text-[#EF4444] transition-colors" data-testid={`delete-exchange-${exchange.id}`}>
              <Trash2 size={14} />
            </button>
          )}
        </div>
      </div>
      {!collapsed && (
        <div className="px-3 pb-3 ml-11">
          {exchange.a && <p className="text-[#8C8F94] text-xs mb-2">À : {exchange.a}</p>}
          <pre className="text-[#ccc] text-sm whitespace-pre-wrap leading-relaxed" style={{ fontFamily: "'Barlow', sans-serif" }}>{exchange.contenu}</pre>
        </div>
      )}
    </div>
  );
}

// ============ FICHE TAB (CSE) ============
function FicheTab({ prospect, onSave, isClient = false }) {
  const [form, setForm] = useState({ ...prospect });
  useEffect(() => { setForm({ ...prospect }); }, [prospect]);
  const handleChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const statutList = isClient ? STATUTS_CLIENT : STATUTS_CSE;

  const inputClass = "w-full bg-[#1a1a1a] border border-[#333] rounded-lg px-3 py-2 text-white text-sm focus:border-[#00E01A] focus:outline-none transition-colors";
  const labelClass = "text-[#8C8F94] text-xs uppercase tracking-wider mb-1 block";

  return (
    <div className="space-y-4" data-testid="prospect-fiche-tab">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className={labelClass} style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Organisation</label>
          <input className={inputClass} value={form.org || ''} onChange={e => handleChange('org', e.target.value)} data-testid="fiche-org" />
        </div>
        <div>
          <label className={labelClass} style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Nom du contact</label>
          <input className={inputClass} value={form.nom || ''} onChange={e => handleChange('nom', e.target.value)} data-testid="fiche-nom" />
        </div>
        <div>
          <label className={labelClass} style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Poste</label>
          <input className={inputClass} value={form.poste || ''} onChange={e => handleChange('poste', e.target.value)} data-testid="fiche-poste" />
        </div>
        <div>
          <label className={labelClass} style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Email</label>
          <input className={inputClass} type="email" value={form.email || ''} onChange={e => handleChange('email', e.target.value)} data-testid="fiche-email" />
        </div>
        <div>
          <label className={labelClass} style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Téléphone</label>
          <input className={inputClass} value={form.tel || ''} onChange={e => handleChange('tel', e.target.value)} data-testid="fiche-tel" />
        </div>
        <div>
          <label className={labelClass} style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Effectif</label>
          <input className={inputClass} value={form.effectif || ''} onChange={e => handleChange('effectif', e.target.value)} data-testid="fiche-effectif" />
        </div>
        <div>
          <label className={labelClass} style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Statut</label>
          <select className={inputClass} value={form.statut || 'a contacter'} onChange={e => handleChange('statut', e.target.value)} data-testid="fiche-statut">
            {statutList.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
        <div>
          <label className={labelClass} style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Priorité</label>
          <select className={inputClass} value={form.priorite || 'moyenne'} onChange={e => handleChange('priorite', e.target.value)} data-testid="fiche-priorite">
            {PRIORITES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </div>
        <div>
          <label className={labelClass} style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Date de premier contact</label>
          <input className={inputClass} type="date" value={form.dateContact || ''} onChange={e => handleChange('dateContact', e.target.value)} data-testid="fiche-dateContact" />
        </div>
        <div>
          <label className={labelClass} style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Date de suivi</label>
          <input className={inputClass} type="date" value={form.dateSuivi || ''} onChange={e => handleChange('dateSuivi', e.target.value)} data-testid="fiche-dateSuivi" />
        </div>
      </div>

      {/* Configuration summary for client prospects */}
      {prospect.type === 'client' && prospect.configuration_summary && (
        <div className="bg-[#0a1a0a] border border-[#00E01A]/20 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <FileText size={16} className="text-[#00E01A]" />
            <h4 className="text-white text-sm font-semibold" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              Configuration demandée — {prospect.brand ? prospect.brand.charAt(0).toUpperCase() + prospect.brand.slice(1) : ''}
            </h4>
            {prospect.price_ht > 0 && (
              <span className="ml-auto text-[#00E01A] font-bold text-sm">
                {prospect.price_ht.toLocaleString('fr-FR')}€ HT
              </span>
            )}
          </div>
          <pre className="text-[#ccc] text-sm whitespace-pre-wrap leading-relaxed" style={{ fontFamily: "'Barlow', sans-serif" }}>
            {prospect.configuration_summary}
          </pre>
        </div>
      )}

      {/* Axonaut integration info */}
      {prospect.axonaut_company_id && (
        <div className="bg-[#0f0a1a] border border-[#8B5CF6]/20 rounded-xl p-4" data-testid="axonaut-section">
          <div className="flex items-center gap-2 mb-3">
            <ExternalLink size={16} className="text-[#8B5CF6]" />
            <h4 className="text-white text-sm font-semibold" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              Axonaut
            </h4>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="flex items-center gap-2 p-2 bg-[#0B0B0B] rounded-lg">
              <span className="text-[#8C8F94] text-xs">Client Axonaut</span>
              <a href={`https://axonaut.com/company/${prospect.axonaut_company_id}`} target="_blank" rel="noopener noreferrer"
                className="ml-auto text-[#8B5CF6] text-xs font-semibold hover:underline flex items-center gap-1"
                data-testid="axonaut-company-link">
                <ExternalLink size={10} /> Voir le client
              </a>
            </div>
            {prospect.axonaut_quotation_id && (
              <div className="flex items-center gap-2 p-2 bg-[#0B0B0B] rounded-lg">
                <span className="text-[#8C8F94] text-xs">Devis Axonaut</span>
                <a href={`https://axonaut.com/quotation-view/${prospect.axonaut_quotation_id}`} target="_blank" rel="noopener noreferrer"
                  className="ml-auto text-[#8B5CF6] text-xs font-semibold hover:underline flex items-center gap-1"
                  data-testid="axonaut-quotation-link">
                  <ExternalLink size={10} /> {prospect.axonaut_quotation_number || 'Voir le devis'}
                </a>
              </div>
            )}
          </div>
          {prospect.address && (
            <div className="mt-2 p-2 bg-[#0B0B0B] rounded-lg text-xs text-[#8C8F94]">
              {prospect.address}{prospect.zip_code ? `, ${prospect.zip_code}` : ''}{prospect.city ? ` ${prospect.city}` : ''}
            </div>
          )}
        </div>
      )}

      <div>
        <label className={labelClass} style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Notes</label>
        <textarea className={inputClass + " min-h-[80px]"} value={form.notes || ''} onChange={e => handleChange('notes', e.target.value)} data-testid="fiche-notes" />
      </div>
      <button
        onClick={() => onSave(form)}
        className="flex items-center gap-2 bg-[#00E01A] hover:bg-[#00B815] text-black font-semibold px-5 py-2.5 rounded-full transition-all shadow-[0_0_15px_rgba(0,224,26,0.3)]"
        data-testid="fiche-save-btn"
      >
        <Save size={16} /> Sauvegarder
      </button>
    </div>
  );
}

// ============ GMAIL SYNC BAR ============
function GmailSyncBar({ prospect, onSync }) {
  const { api } = useAuth();
  const [gmailConnected, setGmailConnected] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkStatus = async () => {
      try {
        const res = await api.get('/gmail/status');
        setGmailConnected(res.data.connected);
      } catch { setGmailConnected(false); }
      finally { setLoading(false); }
    };
    checkStatus();
  }, [api]);

  const handleConnect = async () => {
    try {
      const res = await api.get('/oauth/gmail/login');
      window.open(res.data.url, '_blank', 'width=600,height=700');
      toast.success("Fenêtre de connexion Gmail ouverte. Autorisez l'accès puis revenez ici.");
      const interval = setInterval(async () => {
        try {
          const statusRes = await api.get('/gmail/status');
          if (statusRes.data.connected) {
            clearInterval(interval);
            setGmailConnected(true);
            toast.success('Gmail connecté !');
          }
        } catch {}
      }, 3000);
      setTimeout(() => clearInterval(interval), 120000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur connexion Gmail');
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try { await onSync(); } finally { setSyncing(false); }
  };

  if (loading) return null;

  return (
    <div className="flex items-center justify-between bg-[#0a0a0a] rounded-xl border border-[#222] p-3" data-testid="gmail-sync-bar">
      <div className="flex items-center gap-3">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${gmailConnected ? 'bg-[#142915]' : 'bg-[#1D2327]'}`}>
          <Mail size={16} className={gmailConnected ? 'text-[#00E01A]' : 'text-[#8C8F94]'} />
        </div>
        <div>
          <p className="text-white text-xs font-medium">Synchronisation Gmail</p>
          <p className="text-[#52555A] text-[10px]">
            {gmailConnected ? `Emails avec ${prospect.email}` : 'Connectez Gmail pour synchroniser'}
          </p>
        </div>
      </div>
      {gmailConnected ? (
        <button onClick={handleSync} disabled={syncing || !prospect.email}
          className="flex items-center gap-1.5 bg-[#1D2327] hover:bg-[#2C3338] text-white border border-[#2C3338] px-3 py-1.5 rounded-full text-xs transition-all disabled:opacity-50"
          data-testid="gmail-sync-prospect-btn">
          <Mail size={12} className={syncing ? 'animate-pulse' : ''} />
          {syncing ? 'Synchronisation...' : 'Synchroniser'}
        </button>
      ) : (
        <button onClick={handleConnect}
          className="flex items-center gap-1.5 bg-[#00E01A] hover:bg-[#00B815] text-black font-semibold px-3 py-1.5 rounded-full text-xs transition-all"
          data-testid="gmail-connect-prospect-btn">
          <Mail size={12} /> Connecter Gmail
        </button>
      )}
    </div>
  );
}

// ============ EXCHANGES TAB ============
function ExchangesTab({ prospect, exchanges, onAddExchange, onDeleteExchange, onGmailSync }) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    type: 'email', date: new Date().toISOString().split('T')[0],
    auteur: `Riding Watt <${RW_EMAIL}>`, sujet: '', a: prospect?.email || '', contenu: '',
  });

  const handleSubmit = () => {
    if (!form.contenu.trim()) { toast.error("Le contenu est requis"); return; }
    onAddExchange(form);
    setForm({ type: 'email', date: new Date().toISOString().split('T')[0], auteur: `Riding Watt <${RW_EMAIL}>`, sujet: '', a: prospect?.email || '', contenu: '' });
    setShowForm(false);
  };

  const inputClass = "w-full bg-[#1a1a1a] border border-[#333] rounded-lg px-3 py-2 text-white text-sm focus:border-[#00E01A] focus:outline-none transition-colors";
  const labelClass = "text-[#8C8F94] text-xs uppercase tracking-wider mb-1 block";

  return (
    <div className="space-y-3" data-testid="prospect-exchanges-tab">
      {prospect?.email && onGmailSync && (
        <GmailSyncBar prospect={prospect} onSync={onGmailSync} />
      )}
      {exchanges.map(ex => (
        <ExchangeCard key={ex.id} exchange={ex} onDelete={onDeleteExchange} />
      ))}
      {!showForm ? (
        <button onClick={() => setShowForm(true)}
          className="w-full border border-dashed border-[#333] rounded-lg p-3 text-[#8C8F94] hover:text-[#00E01A] hover:border-[#00E01A] transition-colors flex items-center justify-center gap-2"
          data-testid="add-exchange-btn">
          <Plus size={16} /> Ajouter un échange
        </button>
      ) : (
        <div className="bg-[#111111] border border-[#222] rounded-xl p-4 space-y-3" data-testid="new-exchange-form">
          <div className="flex items-center justify-between">
            <h4 className="text-white font-semibold text-sm" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Nouvel échange</h4>
            <button onClick={() => setShowForm(false)} className="text-[#8C8F94] hover:text-white"><X size={16} /></button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass} style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Type</label>
              <select className={inputClass} value={form.type} onChange={e => setForm(p => ({ ...p, type: e.target.value }))} data-testid="exchange-type">
                {EXCHANGE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className={labelClass} style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Date</label>
              <input className={inputClass} type="date" value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} data-testid="exchange-date" />
            </div>
          </div>
          <div>
            <label className={labelClass} style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Auteur</label>
            <input className={inputClass} value={form.auteur} onChange={e => setForm(p => ({ ...p, auteur: e.target.value }))} data-testid="exchange-auteur" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass} style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Sujet</label>
              <input className={inputClass} value={form.sujet} onChange={e => setForm(p => ({ ...p, sujet: e.target.value }))} data-testid="exchange-sujet" />
            </div>
            <div>
              <label className={labelClass} style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Destinataire</label>
              <input className={inputClass} value={form.a} onChange={e => setForm(p => ({ ...p, a: e.target.value }))} data-testid="exchange-a" />
            </div>
          </div>
          <div>
            <label className={labelClass} style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Contenu</label>
            <textarea className={inputClass + " min-h-[120px]"} value={form.contenu} onChange={e => setForm(p => ({ ...p, contenu: e.target.value }))} data-testid="exchange-contenu" />
          </div>
          <button onClick={handleSubmit}
            className="flex items-center gap-2 bg-[#00E01A] hover:bg-[#00B815] text-black font-semibold px-5 py-2.5 rounded-full transition-all shadow-[0_0_15px_rgba(0,224,26,0.3)]"
            data-testid="submit-exchange-btn">
            <Send size={16} /> Envoyer
          </button>
        </div>
      )}
    </div>
  );
}

// ============ RELANCE TAB ============
function RelanceTab({ prospect, onSave }) {
  const [dateSuivi, setDateSuivi] = useState(prospect?.dateSuivi || '');
  useEffect(() => { setDateSuivi(prospect?.dateSuivi || ''); }, [prospect]);
  const overdue = isOverdue(dateSuivi);
  const soon = isSoon(dateSuivi);

  return (
    <div className="space-y-4" data-testid="prospect-relance-tab">
      <div>
        <label className="text-[#8C8F94] text-xs uppercase tracking-wider mb-2 block" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
          Prochaine date de suivi
        </label>
        <input type="date" className="bg-[#1a1a1a] border border-[#333] rounded-lg px-4 py-3 text-white text-sm focus:border-[#00E01A] focus:outline-none transition-colors"
          value={dateSuivi} onChange={e => setDateSuivi(e.target.value)} data-testid="relance-date" />
      </div>
      {dateSuivi && (
        <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold ${
          overdue ? 'bg-[#2A1515] text-[#EF4444]' : soon ? 'bg-[#2A2A0A] text-[#F59E0B]' : 'bg-[#0a2e0a] text-[#00E01A]'
        }`} data-testid="relance-chip">
          {overdue ? <AlertTriangle size={14} /> : <Clock size={14} />}
          {overdue ? 'En retard' : soon ? 'Bientôt' : 'Planifiée'}
          <span className="text-xs opacity-75 ml-1">{new Date(dateSuivi + 'T00:00:00').toLocaleDateString('fr-FR')}</span>
        </div>
      )}
      <button onClick={() => onSave({ dateSuivi })}
        className="flex items-center gap-2 bg-[#00E01A] hover:bg-[#00B815] text-black font-semibold px-5 py-2.5 rounded-full transition-all shadow-[0_0_15px_rgba(0,224,26,0.3)]"
        data-testid="relance-save-btn">
        <Save size={16} /> Sauvegarder la date
      </button>
    </div>
  );
}

const TASK_STATUSES = [
  { id: 'todo', label: 'A faire', color: '#FBBF24', bg: 'bg-yellow-500/10', border: 'border-yellow-500/30', text: 'text-yellow-400' },
  { id: 'in_progress', label: 'En cours', color: '#3B82F6', bg: 'bg-blue-500/10', border: 'border-blue-500/30', text: 'text-blue-400' },
  { id: 'to_validate', label: 'A valider', color: '#F97316', bg: 'bg-orange-500/10', border: 'border-orange-500/30', text: 'text-orange-400' },
  { id: 'done', label: 'Termine', color: '#22C55E', bg: 'bg-green-500/10', border: 'border-green-500/30', text: 'text-green-400' },
];

const TASK_CATEGORIES = [
  { id: 'reseaux_sociaux', label: 'Reseaux sociaux' },
  { id: 'emailing', label: 'Emailing' },
  { id: 'site_web_seo', label: 'Site web / SEO' },
  { id: 'evenements', label: 'Evenements' },
  { id: 'print', label: 'Print / Flyers' },
  { id: 'partenariats', label: 'Partenariats' },
  { id: 'photo_video', label: 'Photo / Video' },
  { id: 'autre', label: 'Autre' },
];

const TASK_PRIORITIES = [
  { id: 'low', label: 'Basse', color: '#6B7280' },
  { id: 'medium', label: 'Moyenne', color: '#3B82F6' },
  { id: 'high', label: 'Haute', color: '#F97316' },
  { id: 'urgent', label: 'Urgente', color: '#EF4444' },
];

const getTaskStatus = (id) => TASK_STATUSES.find(s => s.id === id) || TASK_STATUSES[0];
const getTaskCategory = (id) => TASK_CATEGORIES.find(c => c.id === id) || TASK_CATEGORIES[7];
const getTaskPriority = (id) => TASK_PRIORITIES.find(p => p.id === id) || TASK_PRIORITIES[1];

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ============ MARKETING TASKS TAB ============
function MarketingTasksTab({ prospect, token }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    title: '', description: '', category: 'autre', priority: 'medium',
    deadline: '', start_date: '',
  });

  const fetchTasks = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/marketing/tasks/by-prospect/${prospect.id}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (r.ok) setTasks(await r.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [prospect.id, token]);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  const handleCreate = async () => {
    if (!form.title.trim()) return toast.error("Titre requis");
    try {
      const body = {
        ...form,
        status: 'todo',
        deadline: form.deadline ? new Date(form.deadline).toISOString() : null,
        start_date: form.start_date ? new Date(form.start_date).toISOString() : null,
        linked_prospect_id: prospect.id,
        linked_prospect_type: prospect.type || 'cse',
        linked_prospect_name: prospect.org || prospect.nom || '',
      };
      const r = await fetch(`${API_URL}/api/marketing/tasks`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error(await r.text());
      toast.success('Tache marketing creee et liee au prospect');
      setShowForm(false);
      setForm({ title: '', description: '', category: 'autre', priority: 'medium', deadline: '', start_date: '' });
      fetchTasks();
    } catch { toast.error('Erreur creation tache'); }
  };

  const inputClass = "w-full bg-[#1a1a1a] border border-[#333] rounded-lg px-3 py-2 text-white text-sm focus:border-[#00E01A] focus:outline-none transition-colors";
  const labelClass = "text-[#8C8F94] text-xs uppercase tracking-wider mb-1 block";

  if (loading) return <div className="flex justify-center py-6"><div className="w-5 h-5 border-2 border-[#00E01A] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-3" data-testid="prospect-marketing-tasks-tab">
      {tasks.length === 0 && !showForm && (
        <div className="text-center py-6">
          <Megaphone size={28} className="mx-auto text-[#333] mb-2" />
          <p className="text-[#8C8F94] text-sm">Aucune tache marketing liee</p>
        </div>
      )}

      {tasks.map(t => {
        const st = getTaskStatus(t.status);
        const pri = getTaskPriority(t.priority);
        const cat = getTaskCategory(t.category);
        return (
          <div key={t.id} className={`p-3 rounded-lg border ${st.bg} ${st.border} transition-all`} data-testid={`prospect-task-${t.id}`}>
            <div className="flex items-start justify-between mb-1">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: pri.color }}></div>
                <h4 className={`text-sm font-medium ${st.text}`}>{t.title}</h4>
              </div>
              <span className={`text-[10px] px-1.5 py-0.5 rounded ${st.bg} ${st.text} border ${st.border}`}>{st.label}</span>
            </div>
            {t.description && <p className="text-xs text-[#8C8F94] ml-4 mb-1">{t.description}</p>}
            <div className="flex items-center gap-2 ml-4 text-[10px] text-[#8C8F94]">
              <span>{cat.label}</span>
              {t.deadline && <span>Deadline: {new Date(t.deadline).toLocaleDateString('fr-FR')}</span>}
            </div>
          </div>
        );
      })}

      {!showForm ? (
        <button onClick={() => setShowForm(true)}
          className="w-full border border-dashed border-[#333] rounded-lg p-3 text-[#8C8F94] hover:text-[#00E01A] hover:border-[#00E01A] transition-colors flex items-center justify-center gap-2"
          data-testid="add-marketing-task-btn">
          <Plus size={16} /> Ajouter une tache marketing
        </button>
      ) : (
        <div className="bg-[#0a0a0a] border border-[#222] rounded-xl p-4 space-y-3" data-testid="new-marketing-task-form">
          <div className="flex items-center justify-between">
            <h4 className="text-white font-semibold text-sm flex items-center gap-2" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              <Megaphone size={14} className="text-[#00E01A]" /> Nouvelle tache marketing
            </h4>
            <button onClick={() => setShowForm(false)} className="text-[#8C8F94] hover:text-white"><X size={16} /></button>
          </div>
          <div>
            <label className={labelClass} style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Titre *</label>
            <input className={inputClass} value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Ex: Shooting photo produit" data-testid="mkt-task-title" />
          </div>
          <div>
            <label className={labelClass} style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Description</label>
            <textarea className={inputClass + " min-h-[60px]"} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} data-testid="mkt-task-desc" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass} style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Categorie</label>
              <select className={inputClass} value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} data-testid="mkt-task-cat">
                {TASK_CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className={labelClass} style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Priorite</label>
              <select className={inputClass} value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))} data-testid="mkt-task-priority">
                {TASK_PRIORITIES.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass} style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Date debut</label>
              <input type="date" className={inputClass} value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} data-testid="mkt-task-start" />
            </div>
            <div>
              <label className={labelClass} style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Deadline</label>
              <input type="date" className={inputClass} value={form.deadline} onChange={e => setForm(f => ({ ...f, deadline: e.target.value }))} data-testid="mkt-task-deadline" />
            </div>
          </div>
          <button onClick={handleCreate}
            className="flex items-center gap-2 bg-[#00E01A] hover:bg-[#00B815] text-black font-semibold px-5 py-2.5 rounded-full transition-all shadow-[0_0_15px_rgba(0,224,26,0.3)]"
            data-testid="submit-marketing-task-btn">
            <Plus size={16} /> Creer la tache
          </button>
        </div>
      )}
    </div>
  );
}

// ============ PROSPECT TASKS TAB ============
function ProspectTasksTab({ prospect, api }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newTitle, setNewTitle] = useState('');
  const [newPriority, setNewPriority] = useState('normale');
  const [newDueDate, setNewDueDate] = useState('');
  const [showPresets, setShowPresets] = useState(false);

  const fetchTasks = useCallback(async () => {
    try {
      const res = await api.get(`/prospects/${prospect.id}/tasks`);
      setTasks(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [prospect.id, api]);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  const handleCreate = async (title) => {
    const t = title || newTitle;
    if (!t.trim()) return;
    try {
      await api.post(`/prospects/${prospect.id}/tasks`, {
        title: t, done: false, priority: newPriority, due_date: newDueDate || null,
      });
      setNewTitle(''); setNewDueDate(''); setNewPriority('normale');
      setShowPresets(false);
      fetchTasks();
    } catch { toast.error('Erreur creation tache'); }
  };

  const handleToggle = async (task) => {
    try {
      await api.put(`/prospects/${prospect.id}/tasks/${task.id}`, {
        ...task, done: !task.done,
      });
      fetchTasks();
    } catch { toast.error('Erreur mise a jour'); }
  };

  const handleDelete = async (taskId) => {
    try {
      await api.delete(`/prospects/${prospect.id}/tasks/${taskId}`);
      fetchTasks();
    } catch { toast.error('Erreur suppression'); }
  };

  if (loading) return <div className="flex justify-center py-6"><div className="w-5 h-5 border-2 border-[#00E01A] border-t-transparent rounded-full animate-spin" /></div>;

  const pending = tasks.filter(t => !t.done);
  const completed = tasks.filter(t => t.done);

  return (
    <div className="space-y-3" data-testid="prospect-tasks-tab">
      {/* Quick add */}
      <div className="flex gap-2">
        <input
          className="flex-1 bg-[#1a1a1a] border border-[#333] rounded-lg px-3 py-2 text-white text-sm focus:border-[#00E01A] focus:outline-none"
          placeholder="Ajouter une tache..."
          value={newTitle}
          onChange={e => setNewTitle(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleCreate()}
          data-testid="task-input"
        />
        <button onClick={() => setShowPresets(!showPresets)}
          className="px-3 py-2 rounded-lg bg-[#1a1a1a] border border-[#333] text-[#8C8F94] hover:text-white transition-colors"
          data-testid="task-presets-btn" title="Taches rapides">
          <List size={16} />
        </button>
        <button onClick={() => handleCreate()}
          className="px-4 py-2 rounded-lg bg-[#00E01A] text-black font-semibold text-sm hover:bg-[#00B815] transition-colors"
          data-testid="task-add-btn">
          <Plus size={16} />
        </button>
      </div>

      {/* Presets */}
      {showPresets && (
        <div className="grid grid-cols-2 gap-1.5" data-testid="task-presets">
          {TASK_PRESETS.map(p => (
            <button key={p} onClick={() => handleCreate(p)}
              className="text-left px-3 py-2 rounded-lg bg-[#1a1a1a] border border-[#222] text-[#8C8F94] text-xs hover:text-white hover:border-[#00E01A]/30 transition-all">
              + {p}
            </button>
          ))}
        </div>
      )}

      {/* Pending tasks */}
      {pending.length > 0 && (
        <div className="space-y-1.5">
          {pending.map(t => (
            <div key={t.id}
              className="flex items-center gap-3 p-3 rounded-lg bg-[#1a1a1a] border border-[#222] group hover:border-[#333] transition-all"
              data-testid={`task-${t.id}`}>
              <button onClick={() => handleToggle(t)}
                className="w-5 h-5 rounded-full border-2 border-[#555] hover:border-[#00E01A] transition-colors flex-shrink-0 flex items-center justify-center"
                data-testid={`task-toggle-${t.id}`}>
              </button>
              <span className="flex-1 text-white text-sm">{t.title}</span>
              {t.due_date && (
                <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                  t.due_date < new Date().toISOString().split('T')[0] ? 'bg-[#2A1515] text-[#EF4444]' : 'bg-[#1a1a2e] text-[#8C8F94]'
                }`}>{new Date(t.due_date + 'T00:00:00').toLocaleDateString('fr-FR')}</span>
              )}
              <button onClick={() => handleDelete(t.id)}
                className="opacity-0 group-hover:opacity-100 text-[#8C8F94] hover:text-[#EF4444] transition-all"
                data-testid={`task-delete-${t.id}`}>
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Completed tasks */}
      {completed.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-[#555] text-[10px] uppercase tracking-wider mt-2">Terminees ({completed.length})</p>
          {completed.map(t => (
            <div key={t.id}
              className="flex items-center gap-3 p-3 rounded-lg bg-[#0d1a0d] border border-[#00E01A]/10 group transition-all"
              data-testid={`task-done-${t.id}`}>
              <button onClick={() => handleToggle(t)}
                className="w-5 h-5 rounded-full bg-[#00E01A] flex-shrink-0 flex items-center justify-center"
                data-testid={`task-untoggle-${t.id}`}>
                <Check size={12} className="text-black" />
              </button>
              <span className="flex-1 text-[#555] text-sm line-through">{t.title}</span>
              <button onClick={() => handleDelete(t.id)}
                className="opacity-0 group-hover:opacity-100 text-[#555] hover:text-[#EF4444] transition-all">
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      {tasks.length === 0 && (
        <div className="text-center py-6">
          <CheckCircle2 size={28} className="mx-auto text-[#333] mb-2" />
          <p className="text-[#8C8F94] text-sm">Aucune tache. Ajoutez-en une ci-dessus.</p>
        </div>
      )}
    </div>
  );
}

// ============ KANBAN VIEW ============
function KanbanView({ prospects, onToggle, expandedId, onSave, onDelete, onAddExchange, onDeleteExchange, cardRefs, onStatusChange }) {
  const columns = STATUTS_CLIENT;
  const [draggedId, setDraggedId] = useState(null);
  const [dragOverCol, setDragOverCol] = useState(null);

  const handleDragStart = (e, prospectId) => {
    setDraggedId(prospectId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', prospectId);
  };

  const handleDragOver = (e, colValue) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverCol(colValue);
  };

  const handleDragLeave = () => {
    setDragOverCol(null);
  };

  const handleDrop = async (e, colValue) => {
    e.preventDefault();
    setDragOverCol(null);
    const prospectId = e.dataTransfer.getData('text/plain') || draggedId;
    if (!prospectId) return;
    const prospect = prospects.find(p => p.id === prospectId);
    if (!prospect) return;
    // Don't update if same column
    if (matchStatut(prospect.statut, colValue, columns.find(c => c.value === colValue)?.aliases || [])) {
      setDraggedId(null);
      return;
    }
    try {
      await onStatusChange(prospectId, colValue);
    } catch (err) {
      console.error('Status change error:', err);
    }
    setDraggedId(null);
  };

  const handleDragEnd = () => {
    setDraggedId(null);
    setDragOverCol(null);
  };

  return (
    <div className="flex gap-3 overflow-x-auto pb-4" data-testid="kanban-view" style={{ minHeight: '400px' }}>
      {columns.map(col => {
        const colProspects = prospects.filter(p => matchStatut(p.statut, col.value, col.aliases));
        const isOver = dragOverCol === col.value;
        return (
          <div key={col.value}
            className={`flex-shrink-0 w-72 rounded-xl border overflow-hidden transition-all duration-200 ${
              isOver ? 'border-[' + col.color + '] bg-[#111]' : 'border-[#222] bg-[#0a0a0a]'
            }`}
            onDragOver={(e) => handleDragOver(e, col.value)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, col.value)}
          >
            <div className="p-3 border-b border-[#222] flex items-center gap-2">
              <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: col.color }} />
              <span className="text-white text-xs font-semibold uppercase tracking-wider" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                {col.label}
              </span>
              <span className="ml-auto text-[#555] text-xs font-mono">{colProspects.length}</span>
            </div>
            <div className={`p-2 space-y-2 max-h-[600px] overflow-y-auto min-h-[80px] transition-all ${isOver ? 'bg-[' + col.color + '10]' : ''}`}>
              {colProspects.length === 0 && !isOver && (
                <div className="p-4 text-center">
                  <p className="text-[#333] text-xs">Aucun prospect</p>
                </div>
              )}
              {isOver && colProspects.length === 0 && (
                <div className="p-4 text-center border-2 border-dashed rounded-lg" style={{ borderColor: col.color + '40' }}>
                  <p className="text-xs" style={{ color: col.color }}>Deposer ici</p>
                </div>
              )}
              {colProspects.map((p, idx) => {
                const prioStyle = getPrioriteStyle(p.priorite);
                const isDragged = draggedId === p.id;
                return (
                  <div key={p.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, p.id)}
                    onDragEnd={handleDragEnd}
                    className={`bg-[#111111] rounded-lg border border-[#222] p-3 transition-all cursor-grab active:cursor-grabbing ${
                      isDragged ? 'opacity-40 scale-95' : 'hover:border-[#333]'
                    }`}
                    onClick={() => !draggedId && onToggle(p.id)}
                    ref={el => { cardRefs.current[p.id] = el; }}
                    data-testid={`kanban-card-${p.id}`}>
                    <div className="flex items-start gap-2 mb-2">
                      <div className={`w-2 h-2 rounded-full flex-shrink-0 mt-1.5 ${prioStyle.pulse ? 'animate-pulse' : ''}`}
                        style={{ backgroundColor: prioStyle.color, boxShadow: prioStyle.glow }} />
                      <p className="text-white text-sm font-medium leading-tight">{p.org || p.nom}</p>
                    </div>
                    {p.email && <p className="text-[#8C8F94] text-[10px] truncate mb-1">{p.email}</p>}
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {p.brand && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#3B82F6]/15 text-[#3B82F6] font-semibold">{p.brand}</span>
                      )}
                      {p.task_count > 0 && (
                        <span className={`text-[9px] px-1.5 py-0.5 rounded font-semibold ${
                          p.task_done === p.task_count ? 'bg-[#00E01A]/15 text-[#00E01A]' : 'bg-[#F59E0B]/15 text-[#F59E0B]'
                        }`}>{p.task_done}/{p.task_count} taches</span>
                      )}
                      {p.exchange_count > 0 && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#3B82F6]/10 text-[#3B82F6] font-semibold">{p.exchange_count} ech.</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ============ PROSPECT CARD ============
function ProspectCard({ prospect, index, expanded, onToggle, onSave, onDelete, onAddExchange, onDeleteExchange, cardRef }) {
  const [activeTab, setActiveTab] = useState('fiche');
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const { api, token } = useAuth();

  const isClient = prospect.type === 'client';
  const statutStyle = getStatutStyle(prospect.statut, isClient);
  const prioStyle = getPrioriteStyle(prospect.priorite);
  const avatarColor = AVATAR_COLORS[index % AVATAR_COLORS.length];
  const initials = getInitials(prospect.org || prospect.nom);
  const overdue = isOverdue(prospect.dateSuivi);
  const soon = isSoon(prospect.dateSuivi);

  const loadDetail = useCallback(async () => {
    if (!expanded) return;
    setLoadingDetail(true);
    try {
      const res = await api.get(`/prospects/${prospect.id}`);
      setDetail(res.data);
    } catch { toast.error("Erreur chargement détails"); }
    finally { setLoadingDetail(false); }
  }, [expanded, prospect.id, api]);

  useEffect(() => { loadDetail(); }, [loadDetail]);

  const handleSaveFiche = async (formData) => {
    try {
      await onSave(prospect.id, formData);
      loadDetail();
      toast.success("Prospect mis à jour");
    } catch { toast.error("Erreur mise à jour"); }
  };

  const handleSaveRelance = async ({ dateSuivi }) => {
    try {
      await onSave(prospect.id, { ...prospect, dateSuivi });
      loadDetail();
      toast.success("Date de relance mise à jour");
    } catch { toast.error("Erreur mise à jour"); }
  };

  const handleAddExchange = async (exchangeData) => {
    try {
      await onAddExchange(prospect.id, exchangeData);
      loadDetail();
      toast.success("Échange ajouté");
    } catch { toast.error("Erreur ajout échange"); }
  };

  const handleDeleteExchange = async (exchangeId) => {
    try {
      await onDeleteExchange(prospect.id, exchangeId);
      loadDetail();
      toast.success("Échange supprimé");
    } catch { toast.error("Erreur suppression"); }
  };

  const handleGmailSync = async () => {
    try {
      const res = await api.post(`/gmail/sync-prospect/${prospect.id}`);
      toast.success(`${res.data.synced} email(s) synchronise(s) (${res.data.total} total)`);
      loadDetail();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur synchronisation Gmail'); }
  };

  const handleConvertToClient = async () => {
    if (!window.confirm('Convertir ce prospect en client ?')) return;
    try {
      const res = await api.post(`/prospects/${prospect.id}/convert-to-client`);
      if (res.data.already_exists) {
        toast.info('Un client avec cet email existe deja');
      } else {
        toast.success('Prospect converti en client !');
      }
      loadDetail();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur conversion'); }
  };

  const isDevisAccepte = matchStatut(prospect.statut, 'devis accepte', ['devis accepté']);
  const isConverted = !!prospect.converted_client_id;

  const tabs = [
    { id: 'fiche', label: 'Fiche' },
    { id: 'taches', label: `Taches${prospect.task_count ? ` (${prospect.task_done || 0}/${prospect.task_count})` : ''}` },
    { id: 'echanges', label: 'Echanges' },
    { id: 'relance', label: 'Relance' },
  ];

  return (
    <div ref={cardRef}
      className={`bg-[#111111] rounded-xl border transition-all duration-300 ${expanded ? 'border-[#00E01A]/30 shadow-[0_0_20px_rgba(0,224,26,0.08)]' : 'border-[#222] hover:border-[#333]'}`}
      data-testid={`prospect-card-${prospect.id}`}>
      {/* Header */}
      <div className="flex items-center gap-3 p-4 cursor-pointer select-none" onClick={() => onToggle(prospect.id)} data-testid={`prospect-header-${prospect.id}`}>
        <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold text-white flex-shrink-0"
          style={{ backgroundColor: isClient ? '#3B82F6' : avatarColor }}>
          {isClient ? <ShoppingCart size={18} /> : initials}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="text-white font-semibold text-sm truncate">{prospect.org || prospect.nom}</h3>
            <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${prioStyle.pulse ? 'animate-pulse' : ''}`} style={{ backgroundColor: prioStyle.color, boxShadow: prioStyle.glow }} title={`Priorite ${prioStyle.label}`} />
            {isClient && prospect.brand && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#3B82F6]/15 text-[#3B82F6] font-semibold flex-shrink-0" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                {prospect.brand.charAt(0).toUpperCase() + prospect.brand.slice(1)}
              </span>
            )}
            {isClient && prospect.price_ht > 0 && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#00E01A]/15 text-[#00E01A] font-semibold flex-shrink-0" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                {prospect.price_ht.toLocaleString('fr-FR')}€ HT
              </span>
            )}
            {prospect.axonaut_quotation_number && (
              <a href={`https://axonaut.com/quotation-view/${prospect.axonaut_quotation_id}`} target="_blank" rel="noopener noreferrer"
                className="text-[10px] px-1.5 py-0.5 rounded bg-[#8B5CF6]/15 text-[#8B5CF6] font-semibold flex-shrink-0 hover:bg-[#8B5CF6]/25 transition-colors flex items-center gap-1"
                style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                onClick={e => e.stopPropagation()}
                data-testid={`axonaut-badge-${prospect.id}`}>
                <ExternalLink size={10} /> Devis {prospect.axonaut_quotation_number}
              </a>
            )}
          </div>
          {prospect.email && (
            <p className="text-[#8C8F94] text-xs truncate">{prospect.email}{prospect.tel ? ` — ${prospect.tel}` : ''}</p>
          )}
          {!prospect.email && prospect.nom && (
            <p className="text-[#8C8F94] text-xs truncate">{prospect.nom}{prospect.poste ? ` — ${prospect.poste}` : ''}</p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {(overdue || soon) && (
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${overdue ? 'bg-[#2A1515] text-[#EF4444]' : 'bg-[#2A2A0A] text-[#F59E0B]'}`} style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              {overdue ? 'Relance en retard' : 'Relance bientôt'}
            </span>
          )}
          {(prospect.exchange_count > 0) && (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#1a1a2e] text-[#3B82F6] font-semibold" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              {prospect.exchange_count} ech.
            </span>
          )}
          {prospect.task_count > 0 && (
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${
              prospect.task_done === prospect.task_count ? 'bg-[#0d1a0d] text-[#00E01A]' : 'bg-[#2A2A0A] text-[#F59E0B]'
            }`} style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              {prospect.task_done}/{prospect.task_count} taches
            </span>
          )}
          {prospect.priorite === 'urgent' && (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#FF0040]/20 text-[#FF0040] font-bold animate-pulse" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              URGENT
            </span>
          )}
          <span className="text-[10px] px-2.5 py-0.5 rounded-full font-semibold"
            style={{ backgroundColor: statutStyle.bg, color: statutStyle.color, fontFamily: "'Barlow Condensed', sans-serif" }}>
            {statutStyle.label}
          </span>
          {expanded ? <ChevronUp size={18} className="text-[#8C8F94]" /> : <ChevronDown size={18} className="text-[#8C8F94]" />}
        </div>
      </div>

      {expanded && (
        <div className="border-t border-[#222] p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex gap-1 flex-1 bg-[#0a0a0a] rounded-lg p-1" data-testid="prospect-tabs">
              {tabs.map(tab => (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 py-2 px-3 rounded-lg text-xs font-semibold transition-all ${activeTab === tab.id ? 'bg-[#00E01A] text-black' : 'text-[#8C8F94] hover:text-white'}`}
                  style={{ fontFamily: "'Barlow Condensed', sans-serif" }} data-testid={`tab-${tab.id}`}>
                  {tab.label}
                </button>
              ))}
            </div>
            <button onClick={() => onDelete(prospect.id)}
              className="ml-3 p-2 rounded-lg text-[#8C8F94] hover:text-[#EF4444] hover:bg-[#2A1515] transition-all"
              title="Supprimer ce prospect"
              data-testid={`delete-prospect-${prospect.id}`}>
              <Trash2 size={16} />
            </button>
          </div>
          {loadingDetail ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-6 h-6 border-2 border-[#00E01A] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <>
              {activeTab === 'fiche' && detail && <FicheTab prospect={detail} onSave={handleSaveFiche} isClient={isClient} />}
              {activeTab === 'echanges' && detail && (
                <ExchangesTab prospect={detail} exchanges={detail.exchanges || []}
                  onAddExchange={handleAddExchange} onDeleteExchange={handleDeleteExchange} onGmailSync={handleGmailSync} />
              )}
              {activeTab === 'relance' && detail && <RelanceTab prospect={detail} onSave={handleSaveRelance} />}
              {activeTab === 'taches' && detail && <ProspectTasksTab prospect={detail} api={api} />}

              {/* Convert to client button */}
              {isClient && (isDevisAccepte || isConverted) && (
                <div className="mt-4 pt-4 border-t border-[#222]">
                  {isConverted ? (
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-[#0d1a0d] border border-[#00E01A]/20">
                      <CheckCircle2 size={18} className="text-[#00E01A]" />
                      <span className="text-[#00E01A] text-sm font-semibold">Converti en client</span>
                      <a href={`/clients/${prospect.converted_client_id}`}
                        className="ml-auto text-[#3B82F6] text-xs hover:underline flex items-center gap-1"
                        data-testid="view-client-link">
                        Voir la fiche client <ArrowRight size={12} />
                      </a>
                    </div>
                  ) : (
                    <button onClick={handleConvertToClient}
                      className="w-full flex items-center justify-center gap-2 bg-[#10B981] hover:bg-[#059669] text-white font-semibold px-5 py-3 rounded-xl transition-all shadow-[0_0_15px_rgba(16,185,129,0.3)]"
                      data-testid="convert-to-client-btn">
                      <UserCheck size={18} /> Convertir en client
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ============ NEW PROSPECT FORM ============
function NewProspectForm({ onSubmit, onCancel, prospectType }) {
  const isClient = prospectType === 'client';
  const [form, setForm] = useState({
    org: '', nom: '', poste: '', email: '', tel: '',
    effectif: '', statut: 'à contacter', priorite: 'moyenne',
    dateContact: '', dateSuivi: '', notes: '', type: prospectType,
  });

  const handleChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = () => {
    if (isClient && !form.nom.trim()) { toast.error("Le nom est requis"); return; }
    if (!isClient && !form.org.trim()) { toast.error("L'organisation est requise"); return; }
    onSubmit(form);
  };

  const inputClass = "w-full bg-[#1a1a1a] border border-[#333] rounded-lg px-3 py-2 text-white text-sm focus:border-[#00E01A] focus:outline-none transition-colors";
  const labelClass = "text-[#8C8F94] text-xs uppercase tracking-wider mb-1 block";

  return (
    <div className="bg-[#111111] rounded-xl border border-[#00E01A]/30 p-5 space-y-4" data-testid="new-prospect-form">
      <div className="flex items-center justify-between">
        <h3 className="text-white font-bold" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.05em' }}>
          {isClient ? 'Nouveau Prospect Client' : 'Nouveau Prospect CSE'}
        </h3>
        <button onClick={onCancel} className="text-[#8C8F94] hover:text-white"><X size={18} /></button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {isClient ? (
          <>
            <div>
              <label className={labelClass} style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Nom complet *</label>
              <input className={inputClass} value={form.nom} onChange={e => { handleChange('nom', e.target.value); handleChange('org', e.target.value); }} data-testid="new-nom" />
            </div>
            <div>
              <label className={labelClass} style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Email</label>
              <input className={inputClass} type="email" value={form.email} onChange={e => handleChange('email', e.target.value)} data-testid="new-email" />
            </div>
            <div>
              <label className={labelClass} style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Téléphone</label>
              <input className={inputClass} value={form.tel} onChange={e => handleChange('tel', e.target.value)} data-testid="new-tel" />
            </div>
          </>
        ) : (
          <>
            <div>
              <label className={labelClass} style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Organisation *</label>
              <input className={inputClass} value={form.org} onChange={e => handleChange('org', e.target.value)} data-testid="new-org" />
            </div>
            <div>
              <label className={labelClass} style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Nom du contact</label>
              <input className={inputClass} value={form.nom} onChange={e => handleChange('nom', e.target.value)} data-testid="new-nom" />
            </div>
            <div>
              <label className={labelClass} style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Poste</label>
              <input className={inputClass} value={form.poste} onChange={e => handleChange('poste', e.target.value)} data-testid="new-poste" />
            </div>
            <div>
              <label className={labelClass} style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Email</label>
              <input className={inputClass} type="email" value={form.email} onChange={e => handleChange('email', e.target.value)} data-testid="new-email" />
            </div>
            <div>
              <label className={labelClass} style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Téléphone</label>
              <input className={inputClass} value={form.tel} onChange={e => handleChange('tel', e.target.value)} data-testid="new-tel" />
            </div>
          </>
        )}
        <div>
          <label className={labelClass} style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Priorité</label>
          <select className={inputClass} value={form.priorite} onChange={e => handleChange('priorite', e.target.value)} data-testid="new-priorite">
            {PRIORITES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label className={labelClass} style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Notes</label>
        <textarea className={inputClass + " min-h-[60px]"} value={form.notes} onChange={e => handleChange('notes', e.target.value)} data-testid="new-notes" />
      </div>
      <button onClick={handleSubmit}
        className="flex items-center gap-2 bg-[#00E01A] hover:bg-[#00B815] text-black font-semibold px-5 py-2.5 rounded-full transition-all shadow-[0_0_15px_rgba(0,224,26,0.3)]"
        data-testid="create-prospect-btn">
        <Plus size={16} /> Créer le prospect
      </button>
    </div>
  );
}

// ============ QUOTE REQUESTS TABLE ============
function QuoteRequestsPanel({ quotes, onConvert, converting }) {
  if (!quotes || quotes.length === 0) {
    return (
      <div className="bg-[#111111] rounded-xl border border-[#222] p-6 text-center">
        <FileText size={32} className="mx-auto text-[#333] mb-2" />
        <p className="text-[#8C8F94] text-sm">Aucune demande de devis</p>
      </div>
    );
  }

  return (
    <div className="bg-[#111111] rounded-xl border border-[#222] overflow-hidden" data-testid="quote-requests-panel">
      <div className="p-4 border-b border-[#222] flex items-center gap-2">
        <FileText size={16} className="text-[#3B82F6]" />
        <h3 className="text-white text-sm font-semibold" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
          Demandes de devis du configurateur ({quotes.length})
        </h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-[#222]">
              <th className="text-left p-3 text-[#8C8F94] text-[10px] font-semibold uppercase" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Client</th>
              <th className="text-left p-3 text-[#8C8F94] text-[10px] font-semibold uppercase" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Marque</th>
              <th className="text-left p-3 text-[#8C8F94] text-[10px] font-semibold uppercase" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Configuration</th>
              <th className="text-right p-3 text-[#8C8F94] text-[10px] font-semibold uppercase" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Prix HT</th>
              <th className="text-left p-3 text-[#8C8F94] text-[10px] font-semibold uppercase" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Date</th>
              <th className="text-center p-3 text-[#8C8F94] text-[10px] font-semibold uppercase" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Axonaut</th>
              <th className="text-center p-3 text-[#8C8F94] text-[10px] font-semibold uppercase" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {quotes.map(q => {
              const labels = q.selection_labels || {};
              const configStr = Object.values(labels).join(', ');
              const brandName = q.brand ? q.brand.charAt(0).toUpperCase() + q.brand.slice(1) : 'Aerofoils';
              return (
                <tr key={q.id} className="border-b border-[#1a1a1a] last:border-0 hover:bg-[#1a1a1a]/50 transition-colors">
                  <td className="p-3">
                    <div className="text-white text-sm font-medium">{q.first_name} {q.last_name}</div>
                    <div className="text-[#8C8F94] text-xs">{q.email}</div>
                  </td>
                  <td className="p-3">
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#3B82F6]/15 text-[#3B82F6] font-semibold" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                      {brandName}
                    </span>
                  </td>
                  <td className="p-3 text-[#8C8F94] text-xs max-w-[200px] truncate">{configStr || '-'}</td>
                  <td className="p-3 text-right text-white font-semibold text-sm">
                    {(q.price || 0).toLocaleString('fr-FR')}€
                  </td>
                  <td className="p-3 text-[#8C8F94] text-xs">
                    {q.created_at ? new Date(q.created_at).toLocaleDateString('fr-FR') : '-'}
                  </td>
                  <td className="p-3 text-center">
                    {q.axonaut_quotation_id ? (
                      <a href={`https://axonaut.com/quotation-view/${q.axonaut_quotation_id}`} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-[10px] px-2.5 py-1 rounded-full bg-[#8B5CF6]/15 text-[#8B5CF6] font-semibold hover:bg-[#8B5CF6]/25 transition-colors"
                        style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                        data-testid={`axonaut-quote-link-${q.id}`}>
                        <ExternalLink size={10} /> Devis
                      </a>
                    ) : (
                      <span className="text-[10px] text-[#555]">-</span>
                    )}
                  </td>
                  <td className="p-3 text-center">
                    {q.has_prospect ? (
                      <span className="text-[10px] px-2.5 py-1 rounded-full bg-[#142915] text-[#00E01A] font-semibold" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                        Converti
                      </span>
                    ) : (
                      <button onClick={() => onConvert(q.id)} disabled={converting === q.id}
                        className="flex items-center gap-1.5 mx-auto bg-[#3B82F6] hover:bg-[#2563EB] text-white font-semibold px-3 py-1.5 rounded-full text-xs transition-all disabled:opacity-50"
                        data-testid={`convert-quote-${q.id}`}>
                        {converting === q.id ? (
                          <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <ArrowRight size={12} />
                        )}
                        Créer prospect
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============ MAIN PAGE ============
export default function Prospecting() {
  const { api } = useAuth();
  const [activeType, setActiveType] = useState('cse');
  const [prospects, setProspects] = useState([]);
  const [stats, setStats] = useState({ total: 0, contactes: 0, en_discussion: 0, partenaires: 0, relances_retard: 0 });
  const [reminders, setReminders] = useState([]);
  const [quoteRequests, setQuoteRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatut, setFilterStatut] = useState('');
  const [filterPriorite, setFilterPriorite] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [showReminders, setShowReminders] = useState(false);
  const [showNewForm, setShowNewForm] = useState(false);
  const [converting, setConverting] = useState(null);
  const [gmailSyncing, setGmailSyncing] = useState(false);
  const [viewMode, setViewMode] = useState('list');
  const cardRefs = useRef({});

  const fetchAll = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      params.append('type', activeType);
      if (search) params.append('search', search);
      if (filterStatut) params.append('statut', filterStatut);
      if (filterPriorite) params.append('priorite', filterPriorite);

      const requests = [
        api.get(`/prospects?${params.toString()}`),
        api.get(`/prospects/stats?type=${activeType}`),
        api.get(`/prospects/reminders?type=${activeType}`),
      ];
      if (activeType === 'client') {
        requests.push(api.get('/prospects/quote-requests'));
      }

      const results = await Promise.all(requests);
      setProspects(results[0].data);
      setStats(results[1].data);
      setReminders(results[2].data);
      if (activeType === 'client' && results[3]) {
        setQuoteRequests(results[3].data);
      }
    } catch (e) {
      console.error('Failed to fetch prospects:', e);
    } finally {
      setLoading(false);
    }
  }, [api, activeType, search, filterStatut, filterPriorite]);

  useEffect(() => {
    setLoading(true);
    setExpandedId(null);
    setShowNewForm(false);
    fetchAll();
  }, [fetchAll]);

  // Seed CSE on first load if empty
  useEffect(() => {
    const seedIfEmpty = async () => {
      try {
        const res = await api.post('/prospects/seed');
        if (res.data.seeded) {
          toast.success("10 prospects CSE initiaux chargés");
          fetchAll();
        }
      } catch {}
    };
    seedIfEmpty();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleToggle = (id) => setExpandedId(prev => prev === id ? null : id);

  const handleSave = async (id, data) => {
    await api.put(`/prospects/${id}`, data);
    fetchAll();
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer ce prospect ?")) return;
    await api.delete(`/prospects/${id}`);
    toast.success("Prospect supprimé");
    fetchAll();
  };

  const handleAddExchange = async (prospectId, exchangeData) => {
    await api.post(`/prospects/${prospectId}/exchanges`, exchangeData);
    fetchAll();
  };

  const handleDeleteExchange = async (prospectId, exchangeId) => {
    await api.delete(`/prospects/${prospectId}/exchanges/${exchangeId}`);
    fetchAll();
  };

  const handleCreateProspect = async (data) => {
    await api.post('/prospects', data);
    toast.success("Prospect créé");
    setShowNewForm(false);
    fetchAll();
  };

  const handleConvertQuote = async (quoteId) => {
    setConverting(quoteId);
    try {
      const res = await api.post(`/prospects/from-quote/${quoteId}`);
      if (res.data.already_exists) {
        toast.info("Un prospect existe deja pour ce devis");
      } else {
        toast.success("Prospect client cree avec succes");
      }
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la conversion');
    } finally {
      setConverting(null);
    }
  };

  const handleGmailSync = async () => {
    setGmailSyncing(true);
    try {
      const res = await api.post('/cron/fetch-gmail-leads');
      const data = res.data;
      if (data.created > 0) {
        toast.success(`${data.created} nouveau(x) prospect(s) cree(s) depuis Gmail`);
      } else if (data.skipped_duplicate > 0) {
        toast.info(`Aucun nouveau prospect. ${data.skipped_duplicate} doublon(s) ignore(s).`);
      } else {
        toast.info("Aucun nouvel email de prospect trouve.");
      }
      fetchAll();
    } catch (e) {
      const detail = e.response?.data?.detail || '';
      if (detail.includes('Gmail non connecte')) {
        toast.error("Gmail non connecte. Connectez votre compte Gmail dans les parametres.");
      } else {
        toast.error(detail || 'Erreur lors de la synchronisation Gmail');
      }
    } finally {
      setGmailSyncing(false);
    }
  };

  const scrollToCard = (id) => {
    setExpandedId(id);
    setTimeout(() => {
      cardRefs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  };

  const handleStatusChange = async (prospectId, newStatut) => {
    try {
      const prospect = prospects.find(p => p.id === prospectId);
      if (!prospect) return;
      await api.put(`/prospects/${prospectId}`, { ...prospect, statut: newStatut });
      toast.success(`Statut mis a jour: ${STATUTS_CLIENT.find(s => s.value === newStatut)?.label || newStatut}`);
      fetchAll();
    } catch (e) {
      toast.error('Erreur changement de statut');
    }
  };

  const reminderCount = reminders.length;
  const isClient = activeType === 'client';

  return (
    <div className="p-4 md:p-6 space-y-5 lg:pt-6 pt-4" data-testid="prospecting-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-white tracking-wide" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
            Prospection
          </h1>
          <p className="text-[#8C8F94] text-sm mt-0.5" style={{ fontFamily: "'Barlow', sans-serif" }}>
            {isClient ? 'Suivi des demandes de devis configurateur' : 'Prospection commerciale — Comités d\'entreprise'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {isClient && (
            <button onClick={handleGmailSync} disabled={gmailSyncing}
              className="flex items-center gap-2 bg-[#1D2327] border border-[#3B82F6]/30 hover:bg-[#3B82F6]/15 text-[#3B82F6] font-semibold px-4 py-2.5 rounded-full transition-all text-sm disabled:opacity-50"
              data-testid="gmail-sync-btn">
              {gmailSyncing ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} />}
              {gmailSyncing ? 'Synchronisation...' : 'Sync Gmail'}
            </button>
          )}
          <div className="relative">
            <button onClick={() => setShowReminders(!showReminders)}
              className={`relative p-2.5 rounded-lg transition-colors ${reminderCount > 0 ? 'bg-[#2A1515] text-[#EF4444] hover:bg-[#3A1515]' : 'bg-[#111111] text-[#8C8F94] hover:text-white'}`}
              data-testid="reminders-bell">
              <Bell size={20} />
              {reminderCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#EF4444] rounded-full text-white text-[10px] font-bold flex items-center justify-center">
                  {reminderCount}
                </span>
              )}
            </button>
            {showReminders && <ReminderPanel reminders={reminders} onClose={() => setShowReminders(false)} onScrollTo={scrollToCard} />}
          </div>
          <button onClick={() => setShowNewForm(true)}
            className="flex items-center gap-2 bg-[#00E01A] hover:bg-[#00B815] text-black font-semibold px-4 py-2.5 rounded-full transition-all shadow-[0_0_15px_rgba(0,224,26,0.3)] text-sm"
            data-testid="new-prospect-btn">
            <Plus size={16} /> Nouveau prospect
          </button>
        </div>
      </div>

      {/* Type Tabs */}
      <div className="flex gap-1 bg-[#0a0a0a] rounded-xl p-1 max-w-md" data-testid="prospect-type-tabs">
        <button onClick={() => setActiveType('cse')}
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-semibold transition-all ${
            activeType === 'cse' ? 'bg-[#F59E0B] text-black shadow-[0_0_12px_rgba(245,158,11,0.3)]' : 'text-[#8C8F94] hover:text-white'
          }`}
          style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
          data-testid="tab-cse">
          <Building2 size={16} /> Prospects CSE
        </button>
        <button onClick={() => setActiveType('client')}
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-semibold transition-all ${
            activeType === 'client' ? 'bg-[#3B82F6] text-white shadow-[0_0_12px_rgba(59,130,246,0.3)]' : 'text-[#8C8F94] hover:text-white'
          }`}
          style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
          data-testid="tab-client">
          <ShoppingCart size={16} /> Prospects Client
        </button>
      </div>

      {/* Stats */}
      <StatsBar stats={stats} isClient={isClient} />

      {/* Quote Requests Panel (only for Client tab) */}
      {isClient && <QuoteRequestsPanel quotes={quoteRequests} onConvert={handleConvertQuote} converting={converting} />}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3" data-testid="prospects-filters">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8C8F94]" />
          <input className="w-full bg-[#111111] border border-[#222] rounded-lg pl-9 pr-3 py-2.5 text-white text-sm placeholder:text-[#555] focus:border-[#00E01A] focus:outline-none transition-colors"
            placeholder={isClient ? "Rechercher un prospect client..." : "Rechercher un prospect CSE..."}
            value={search} onChange={e => setSearch(e.target.value)} data-testid="prospects-search" />
        </div>
        <select className="bg-[#111111] border border-[#222] rounded-lg px-3 py-2.5 text-white text-sm focus:border-[#00E01A] focus:outline-none"
          value={filterStatut} onChange={e => setFilterStatut(e.target.value)} data-testid="filter-statut">
          <option value="">Tous les statuts</option>
          {(isClient ? STATUTS_CLIENT : STATUTS_CSE).map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <select className="bg-[#111111] border border-[#222] rounded-lg px-3 py-2.5 text-white text-sm focus:border-[#00E01A] focus:outline-none"
          value={filterPriorite} onChange={e => setFilterPriorite(e.target.value)} data-testid="filter-priorite">
          <option value="">Toutes les priorites</option>
          {PRIORITES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
        </select>
        {isClient && (
          <div className="flex gap-1 bg-[#0a0a0a] rounded-lg p-0.5 ml-auto">
            <button onClick={() => setViewMode('list')}
              className={`p-2 rounded-md transition-all ${viewMode === 'list' ? 'bg-[#222] text-white' : 'text-[#555] hover:text-white'}`}
              data-testid="view-list-btn" title="Vue liste">
              <List size={16} />
            </button>
            <button onClick={() => setViewMode('kanban')}
              className={`p-2 rounded-md transition-all ${viewMode === 'kanban' ? 'bg-[#222] text-white' : 'text-[#555] hover:text-white'}`}
              data-testid="view-kanban-btn" title="Vue Kanban">
              <LayoutGrid size={16} />
            </button>
          </div>
        )}
      </div>

      {/* New prospect form */}
      {showNewForm && (
        <NewProspectForm onSubmit={handleCreateProspect} onCancel={() => setShowNewForm(false)} prospectType={activeType} />
      )}

      {/* Prospect list or Kanban */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-8 h-8 border-2 border-[#00E01A] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : prospects.length === 0 ? (
        <div className="bg-[#111111] rounded-xl border border-[#222] p-8 text-center">
          <Target size={40} className="mx-auto text-[#333] mb-3" />
          <p className="text-[#8C8F94]">
            {isClient ? 'Aucun prospect client. Convertissez une demande de devis ci-dessus.' : 'Aucun prospect CSE trouve'}
          </p>
        </div>
      ) : isClient && viewMode === 'kanban' ? (
        <KanbanView prospects={prospects} onToggle={handleToggle} expandedId={expandedId}
          onSave={handleSave} onDelete={handleDelete} onAddExchange={handleAddExchange}
          onDeleteExchange={handleDeleteExchange} cardRefs={cardRefs} onStatusChange={handleStatusChange} />
      ) : (
        <div className="space-y-2" data-testid="prospects-list">
          {prospects.map((p, idx) => (
            <ProspectCard key={p.id} prospect={p} index={idx} expanded={expandedId === p.id}
              onToggle={handleToggle} onSave={handleSave} onDelete={handleDelete}
              onAddExchange={handleAddExchange} onDeleteExchange={handleDeleteExchange}
              cardRef={el => { cardRefs.current[p.id] = el; }} />
          ))}
        </div>
      )}
    </div>
  );
}
